// rAF-batched ingest from JetStream into Zustand store.
//
// Bridge sends ~5 sensor frames/s/machine; without batching, each NATS
// message would call useMachineStore.setState() and wake every selector
// synchronously. NATS callbacks come from independent network events
// that don't share a microtask, so React 19's auto-batching wouldn't
// help. Result: jank.
//
// Pattern: queue arrivals (already decoded into a JsEnvelope), flush on
// rAF (~16ms = 60Hz). One render per frame regardless of message volume.
//
// Phase B note: the store's existing ingestBatch signature still takes
// the legacy {Envelope, LogLine} shapes. We adapt JsEnvelope -> legacy
// here so Phase B is self-contained — Phase C will rebuild the store
// to consume JsEnvelope directly and this adapter goes away.

import { useMachineStore, type LogLine } from '@/store/machineStore';
import type { Envelope } from '@/types';
import type { JsEnvelope, SensorReadingPayload, LogPayload, AlertPayload } from './envelope';
import { queueWrite as cacheWrite } from '@/idb/cache';
import { isDisregarded } from '@/sensors/classRegistry';

let pendingSensor:  Envelope[]    = [];
let pendingLog:     LogLine[]     = [];
/** Originals for IDB persistence. Distinct from the legacy adapter
 *  arrays so a hydrate path can fan out into the store + dedup
 *  WITHOUT re-persisting (cache-sourced envelopes are already on disk). */
let pendingPersist: JsEnvelope[]  = [];
/** High-water stream sequence seen between flushes. Hoisted out of
 *  ingest() so a 30k-envelope hydrate loop produces ONE setState at
 *  flush time instead of 30k synchronous ones — the latter froze the
 *  tab and could trip selectors into a render storm. */
let pendingMaxSeq:  number        = 0;
let scheduled = false;

// Min spacing between store flushes. Steady-state sensor traffic
// (~1-2 Hz/machine) arrives slower than this, so a normal live tick
// still flushes on the very next frame — no added latency. But a
// JetStream catch-up replay (tab refocus / page refresh) delivers
// hundreds of frames/sec; without a floor here each one would land on
// its own rAF and trigger a full store rebuild + every-card re-render +
// chart redraw at 60 Hz, which is the "fast-forward stutter". Coalescing
// to ~10 Hz cuts that render storm ~6x while keeping the catch-up paint
// visibly smooth. 100ms is below the human-perceptible-lag floor for a
// telemetry dashboard.
const MIN_FLUSH_INTERVAL_MS = 100;
let lastFlushAt = 0;

// Per-flush dedup. Broker-side `Duplicates: 10m` (agent2 §3) covers the
// publisher-retry case; this Set covers reconnect-replay overlap and the
// future IDB warm-cache seam where the same logical row may arrive from
// two paths. Bounded by FIFO eviction so a long-running tab doesn't grow
// the Set unboundedly. Keyed on (systemId|deviceTimeMs) for sensor rows
// and (systemId|deviceTimeMs|message) for logs.
const MAX_DEDUP_ENTRIES = 50_000;
const dedupSet:   Set<string>   = new Set();
const dedupOrder: string[]      = [];

function dedupCheck(key: string): boolean {
  if (dedupSet.has(key)) return true;
  dedupSet.add(key);
  dedupOrder.push(key);
  if (dedupOrder.length > MAX_DEDUP_ENTRIES) {
    const evicted = dedupOrder.shift();
    if (evicted !== undefined) dedupSet.delete(evicted);
  }
  return false;
}

/** Hand a decoded JetStream envelope to the queue. The decode happened
 *  upstream in `nats/envelope.ts` so this path is just routing, dedup,
 *  adaptation, and IDB-persistence enqueue. */
export function queueEnvelope(env: JsEnvelope): void {
  ingest(env, /* persist */ true);
}

/** Hydration path — cache-sourced envelopes flow through the same dedup
 *  + adapter, but skip the IDB write so we don't churn the disk on boot. */
export function ingestFromCache(env: JsEnvelope): void {
  ingest(env, /* persist */ false);
}

function ingest(env: JsEnvelope, persist: boolean): void {
  // High-water seq tracked locally; commit to the store ONCE per flush
  // (see flush()) so 30k cached envelopes don't fire 30k setStates.
  if (env.streamSeq > pendingMaxSeq) pendingMaxSeq = env.streamSeq;

  let accepted = false;
  switch (env.payload.kind) {
    case 'batch':
      for (const r of env.payload.readings) {
        if (dedupCheck(`s|${env.systemId}|${r.deviceTimeMs}`)) continue;
        pendingSensor.push(toLegacyEnvelope(env, r));
        accepted = true;
      }
      break;
    case 'sensor':
      if (!dedupCheck(`s|${env.systemId}|${env.payload.deviceTimeMs}`)) {
        pendingSensor.push(toLegacyEnvelope(env, env.payload));
        accepted = true;
      }
      break;
    case 'log': {
      const key = `l|${env.systemId}|${env.payload.deviceTimeMs}|${env.payload.message}`;
      if (!dedupCheck(key)) {
        pendingLog.push(toLegacyLogLine(env, env.payload));
        accepted = true;
      }
      break;
    }
    case 'alert':
      // Alerts go to the notification store, not the machine store. Lazy-load
      // to keep the store off the dashboard cold path.
      dispatchAlert(env.payload);
      accepted = true;
      break;
    case 'heartbeat':
      // Heartbeats are intentionally dropped on the dashboard — they only
      // exist for the api's heartbeat watcher to stamp machines.last_seen.
      break;
  }
  if (accepted) {
    if (persist) pendingPersist.push(sanitizeForPersist(env));
    schedule();
  }
}

function toLegacyEnvelope(env: JsEnvelope, reading: SensorReadingPayload): Envelope {
  // Flatten nums + strs back into one map for the legacy store. Phase C
  // rebuilds the store to consume the metric/value separation natively.
  // class=disregard fields are dropped here — the earliest point on the
  // live path where the classified registry is available client-side —
  // so they never enter the store, never render, never chart/alert.
  const data: Record<string, string | number | null> = {};
  for (const [k, v] of Object.entries(reading.nums)) if (!isDisregarded(k)) data[k] = v;
  for (const [k, v] of Object.entries(reading.strs)) if (!isDisregarded(k)) data[k] = v;
  return {
    subject:      env.subject,
    company_slug: env.tenant,
    machine_id:   env.systemId,
    ts:           reading.deviceTimeMs,
    data,
  };
}

/** Same drop applied to the raw envelope before it's written to the IDB
 *  cache, so a disregarded field doesn't persist to disk either — only
 *  what already landed pre-reclassification (see isDisregarded's doc
 *  comment) can ever surface from a stale cache entry. */
function sanitizeForPersist(env: JsEnvelope): JsEnvelope {
  const stripReading = (r: SensorReadingPayload): SensorReadingPayload => {
    const nums: Record<string, number> = {};
    const strs: Record<string, string> = {};
    for (const [k, v] of Object.entries(r.nums)) if (!isDisregarded(k)) nums[k] = v;
    for (const [k, v] of Object.entries(r.strs)) if (!isDisregarded(k)) strs[k] = v;
    return { ...r, nums, strs };
  };
  if (env.payload.kind === 'sensor') {
    return { ...env, payload: stripReading(env.payload) };
  }
  if (env.payload.kind === 'batch') {
    return { ...env, payload: { ...env.payload, readings: env.payload.readings.map(stripReading) } };
  }
  return env;
}

function toLegacyLogLine(env: JsEnvelope, log: LogPayload): LogLine {
  return {
    ts:           log.deviceTimeMs,
    company_slug: env.tenant,
    machine_id:   env.systemId,
    level:        log.level,
    message:      log.message,
    raw:          log.raw,
  };
}

function dispatchAlert(payload: AlertPayload): void {
  const e = payload.event;
  if (typeof e.id !== 'string' || typeof e.firedAt !== 'number') return;
  const fired = new Date(e.firedAt as number).toISOString();
  const row = {
    id:           String(e.id),
    rule_id:      String(e.ruleId  ?? ''),
    machine_key:  String(e.machineKey ?? ''),
    field:        String(e.field   ?? ''),
    value:        typeof e.value === 'number' ? e.value as number : null,
    threshold:    typeof e.threshold === 'number' ? e.threshold as number : null,
    operator:     String(e.operator ?? ''),
    stage_level:  String(e.stageLevel ?? 'info'),
    fired_at:     fired,
  };
  void import('@/store/notificationStore').then((m) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    m.useNotificationStore.getState().ingest(row as any);
  });
}

function schedule(): void {
  if (scheduled) return;
  scheduled = true;
  // Honour the min flush interval. When the last flush was recent (a
  // burst), wait out the remainder before the next rAF so we coalesce
  // instead of flushing every frame. performance.now() (not Date.now)
  // so it can't be perturbed by wall-clock jumps.
  const wait = MIN_FLUSH_INTERVAL_MS - (performance.now() - lastFlushAt);
  if (wait <= 0) {
    requestAnimationFrame(flush);
  } else {
    setTimeout(() => requestAnimationFrame(flush), wait);
  }
}

function flush(): void {
  scheduled = false;
  lastFlushAt = performance.now();
  const sensor  = pendingSensor;
  const logs    = pendingLog;
  const persist = pendingPersist;
  const maxSeq  = pendingMaxSeq;
  if (sensor.length === 0 && logs.length === 0 && persist.length === 0 && maxSeq === 0) return;
  pendingSensor  = [];
  pendingLog     = [];
  pendingPersist = [];
  pendingMaxSeq  = 0;
  // One setState() call covers every queued message. Subscribers wake
  // exactly once for this frame.
  if (sensor.length > 0 || logs.length > 0) {
    useMachineStore.getState().ingestBatch(sensor, logs);
  }
  // High-water stream seq committed once per flush. recordStreamSeq()
  // is a separate set() (it doesn't touch s.machines) so subscribers of
  // s.machines don't re-render just because the watermark advanced.
  if (maxSeq > 0) {
    useMachineStore.getState().recordStreamSeq(maxSeq);
  }
  // Persist asynchronously — IDB writes happen off the React render path.
  // queueWrite() is idempotent on streamSeq, so a hydration-then-live
  // overlap re-puts the same row (cheap; same disk content).
  if (persist.length > 0 && currentPersistTenant != null) {
    cacheWrite(currentPersistTenant, persist);
  }
}

let currentPersistTenant: string | null = null;

/** Set the tenant slug under which subsequent persist calls write. Called
 *  by useJsConsumer on bootstrap so the IDB rows land in the
 *  per-tenant DB. Pass null to disable persistence (e.g. on logout). */
export function setPersistTenant(tenant: string | null): void {
  currentPersistTenant = tenant;
}

/** Drop everything pending. Used on logout / connection drop so we don't
 *  flush stale frames into the new session's store.
 *
 *  We deliberately do NOT clear the dedup set on plain effect cleanup —
 *  StrictMode and HMR both run cleanup while the store keeps its data,
 *  so wiping dedup would let the next consumer's 8h replay re-stack
 *  everything the user already saw. The set is bounded; a true logout
 *  goes through dropDedup() below. */
export function clearQueue(): void {
  pendingSensor  = [];
  pendingLog     = [];
  pendingPersist = [];
  pendingMaxSeq  = 0;
  scheduled      = false;
}

/** Wipe the dedup set entirely. Called on logout / company switch where
 *  we want a clean slate (different scope means previous keys are
 *  meaningless to retain). */
export function dropDedup(): void {
  dedupSet.clear();
  dedupOrder.length = 0;
}
